import { useEffect, useState, type ReactNode } from 'react';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@staff-app/ui';
import { ADMIN_ROLES } from '@staff-app/shared-types';
import { useAuthStore } from '@/stores/auth-store';
import { formatDateTimeLong } from '@/lib/format-date';
import {
  useDunningEmailRecipients,
  useSetDunningEmailExclusion,
  type DunningEmailRecipient,
  type DunningEmailRecipients,
} from '../hooks/useDunningEmails';

/** Who may OPEN the list. The resolver enforces the same set. */
export const DUNNING_EMAILS_VIEW_ROLES: string[] = [
  ADMIN_ROLES.ROOT,
  ADMIN_ROLES.BILLING,
  ADMIN_ROLES.SUPPORT,
];
/** Who may Exclude / Include again. Hiding is a courtesy; the resolver is the control. */
export const DUNNING_EMAILS_EDIT_ROLES: string[] = [ADMIN_ROLES.ROOT, ADMIN_ROLES.BILLING];

export const DUNNING_EMAIL_NOTE_MAX = 500;

/** Compact sizing: one-line badges ("Super Admin" no longer wraps) and short buttons. */
const BADGE = 'whitespace-nowrap px-1.5 py-0 text-[11px] leading-5';
const SMALL_BTN = 'h-7 whitespace-nowrap px-2.5 text-xs';

const TID = 'billing-dunning-emails';
const id = (s: string) => `${TID}-${s}`;

function hasRole(roles: string[] | undefined, allowed: string[]) {
  return !!roles?.some((r) => allowed.includes(r));
}

/**
 * Billing Console → "Dunning emails" button, left of the Dunning badge. Visible to ROOT,
 * BILLING and SUPPORT; disabled (with a tooltip) until the customer resolves to an account.
 */
export function DunningEmailsButton({ accountId, label }: { accountId: string | null; label?: string }) {
  const roles = useAuthStore((s) => s.adminUser?.roles);
  const [open, setOpen] = useState(false);
  if (!hasRole(roles, DUNNING_EMAILS_VIEW_ROLES)) return null;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        disabled={!accountId}
        title={accountId ? undefined : 'Enter a customer Stripe ID linked to an account first'}
        onClick={() => setOpen(true)}
        data-testid={id('btn')}
      >
        {'Dunning emails'}
      </Button>
      {accountId && open ? (
        <DunningEmailsDialog accountId={accountId} label={label} open={open} onClose={() => setOpen(false)} />
      ) : null}
    </>
  );
}

type Editing = { email: string; exclude: boolean };

/**
 * Lists who gets this account's dunning emails and lets ROOT/BILLING exclude or re-include
 * single addresses. The account itself stays in dunning either way.
 */
export function DunningEmailsDialog({
  accountId,
  label,
  open,
  onClose,
}: {
  accountId: string;
  label?: string;
  open: boolean;
  onClose: () => void;
}) {
  const roles = useAuthStore((s) => s.adminUser?.roles);
  const canEdit = hasRole(roles, DUNNING_EMAILS_EDIT_ROLES);

  const { data, loading, error, refetch } = useDunningEmailRecipients(open ? accountId : null);
  const [setExclusion] = useSetDunningEmailExclusion();

  // The mutation returns the whole list; it wins over the query until the dialog reopens.
  const [latest, setLatest] = useState<DunningEmailRecipients | null>(null);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (open) {
      setLatest(null);
      setEditing(null);
      setNote('');
      setBusy(false);
      setSaveError(null);
      setSearch('');
    }
  }, [open, accountId]);

  const recipients = (latest ?? data?.dunningEmailRecipients)?.recipients ?? [];
  const allExcluded = recipients.length > 0 && recipients.every((r) => r.excluded);
  // The row being edited stays visible even when the search no longer matches it.
  const shown = filterRecipients(recipients, search, editing?.email);

  const startEdit = (r: DunningEmailRecipient) => {
    setEditing({ email: r.email, exclude: !r.excluded });
    setNote('');
    setSaveError(null);
  };

  const trimmed = note.trim();
  const noteValid = editing?.exclude
    ? trimmed.length > 0 && trimmed.length <= DUNNING_EMAIL_NOTE_MAX
    : trimmed.length <= DUNNING_EMAIL_NOTE_MAX;
  // Excluding the last address that still gets emails.
  const wouldSilenceAll =
    !!editing?.exclude &&
    recipients.length > 0 &&
    recipients.every((r) => r.excluded || r.email === editing.email);

  const save = async () => {
    if (!editing || !noteValid || busy) return;
    setBusy(true);
    setSaveError(null);
    try {
      const r = (
        await setExclusion({
          variables: {
            accountId,
            email: editing.email,
            excluded: editing.exclude,
            comment: trimmed || null,
          },
        })
      ).data?.setDunningEmailExclusion;
      if (!r) throw new Error('No response from staff-api. Reload to see the current list.');
      setLatest(r);
      setEditing(null);
      setNote('');
    } catch (e) {
      setSaveError((e as Error)?.message ?? 'Failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o || busy ? null : onClose())}>
      <DialogContent className="flex max-h-[85vh] max-w-3xl flex-col" data-testid={id('dialog')}>
        <DialogHeader>
          <DialogTitle className="text-lg">
            {label ? `Dunning emails — ${label}` : 'Dunning emails'}
          </DialogTitle>
          <DialogDescription className="text-[13px]">
            {'The account stays in dunning. Excluded addresses just stop getting dunning emails.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-col gap-2">
          {loading && !latest ? (
            <p className="text-sm text-muted-foreground" data-testid={id('loading')}>
              {'Loading…'}
            </p>
          ) : error && !latest ? (
            <div className="flex items-center gap-2 text-sm text-destructive" data-testid={id('load-error')}>
              <span>{error.message}</span>
              <Button variant="outline" size="sm" onClick={() => void refetch()}>
                {'Retry'}
              </Button>
            </div>
          ) : recipients.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid={id('empty')}>
              {'No dunning email addresses found for this account.'}
            </p>
          ) : (
            <div className="flex min-h-0 flex-col gap-2">
              {allExcluded ? (
                <p className="text-xs text-destructive" data-testid={id('all-excluded')}>
                  {'No one on this account will get dunning emails.'}
                </p>
              ) : null}
              <div className="flex items-center gap-2">
                <Input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search email or role"
                  aria-label="Search dunning emails"
                  className="h-8 text-[13px]"
                  data-testid={id('search')}
                />
                <span className="shrink-0 text-xs text-muted-foreground" data-testid={id('count')}>
                  {search.trim()
                    ? `${shown.length} of ${recipients.length}`
                    : `${recipients.length} ${recipients.length === 1 ? 'address' : 'addresses'}`}
                </span>
              </div>
              {/* Fixed cap: a 25-admin account scrolls here instead of growing the popup. */}
              {/* Stable gutter + right padding: the scrollbar never sits on the row buttons. */}
              <div
                className="max-h-80 overflow-y-auto pr-2 [scrollbar-gutter:stable]"
                data-testid={id('scroll')}
              >
                {shown.length === 0 ? (
                  <p className="py-2 text-[13px] text-muted-foreground" data-testid={id('no-match')}>
                    {`No addresses match "${search.trim()}".`}
                  </p>
                ) : (
                  <table className="w-full text-[13px]" data-testid={id('table')}>
                    <thead className="sticky top-0 z-10 bg-background">
                      <tr className="border-b text-left text-xs text-muted-foreground">
                        <th className="py-1 pr-2 font-medium">{'Email'}</th>
                        <th className="py-1 pr-2 font-medium">{'Who'}</th>
                        <th className="py-1 pr-2 font-medium">{'Status'}</th>
                        {canEdit ? <th className="py-1 font-medium" /> : null}
                      </tr>
                    </thead>
                    <tbody>
                      {shown.map((r) => {
                        const isEditing = editing?.email === r.email;
                        return (
                          <RecipientRow
                            key={r.email}
                            r={r}
                            canEdit={canEdit}
                            disabled={busy}
                            onAction={() => startEdit(r)}
                            editor={
                              isEditing && editing ? (
                                <div className="flex flex-col gap-1 py-2" data-testid={id('editor')}>
                                  <Label htmlFor={id('note')} className="text-[13px]">
                                    {editing.exclude ? 'Note (required)' : 'Note (optional)'}
                                  </Label>
                                  <textarea
                                    id={id('note')}
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    rows={2}
                                    maxLength={DUNNING_EMAIL_NOTE_MAX}
                                    placeholder={
                                      editing.exclude
                                        ? 'e.g. Left the company'
                                        : 'e.g. Asked to get them again'
                                    }
                                    className="w-full rounded-md border bg-background p-2 text-[13px]"
                                    data-testid={id('note')}
                                  />
                                  <p className="text-[11px] text-muted-foreground">
                                    {`${trimmed.length}/${DUNNING_EMAIL_NOTE_MAX}`}
                                  </p>
                                  {wouldSilenceAll ? (
                                    <p
                                      className="text-xs text-destructive"
                                      data-testid={id('silence-warning')}
                                    >
                                      {'No one on this account will get dunning emails.'}
                                    </p>
                                  ) : null}
                                  {saveError ? (
                                    <p className="text-xs text-destructive" data-testid={id('save-error')}>
                                      {saveError}
                                    </p>
                                  ) : null}
                                  <div className="flex justify-end gap-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className={SMALL_BTN}
                                      disabled={busy}
                                      onClick={() => setEditing(null)}
                                      data-testid={id('cancel')}
                                    >
                                      {'Cancel'}
                                    </Button>
                                    <Button
                                      variant={editing.exclude ? 'destructive' : 'default'}
                                      size="sm"
                                      className={SMALL_BTN}
                                      disabled={!noteValid || busy}
                                      onClick={() => void save()}
                                      data-testid={id('confirm')}
                                    >
                                      {busy ? 'Saving…' : editing.exclude ? 'Exclude' : 'Include again'}
                                    </Button>
                                  </div>
                                </div>
                              ) : null
                            }
                          />
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Case-insensitive substring match on the address and its role labels ("admin", "billing").
 * `keep` (the row being edited) always survives, so typing never hides an open editor.
 */
export function filterRecipients(
  recipients: DunningEmailRecipient[],
  query: string,
  keep?: string,
): DunningEmailRecipient[] {
  const q = query.trim().toLowerCase();
  if (!q) return recipients;
  return recipients.filter(
    (r) =>
      r.email === keep ||
      r.email.toLowerCase().includes(q) ||
      r.sources.some((s) => s.toLowerCase().includes(q)),
  );
}

function RecipientRow({
  r,
  canEdit,
  disabled,
  onAction,
  editor,
}: {
  r: DunningEmailRecipient;
  canEdit: boolean;
  disabled: boolean;
  onAction: () => void;
  editor: ReactNode;
}) {
  const by = [r.excludedBy, r.excludedAt ? formatDateTimeLong(r.excludedAt) : null]
    .filter(Boolean)
    .join(', ');
  return (
    <>
      <tr className="border-b align-top" data-testid={id('row')} data-email={r.email}>
        <td className="py-1.5 pr-2 break-all">{r.email}</td>
        <td className="py-1.5 pr-2">
          <div className="flex flex-wrap gap-1">
            {r.sources.map((s) => (
              <Badge key={s} variant="secondary" className={BADGE}>
                {s}
              </Badge>
            ))}
          </div>
        </td>
        <td className="py-1.5 pr-2">
          {r.excluded ? (
            <div className="flex flex-col gap-0.5">
              <Badge variant="destructive" className={`w-fit ${BADGE}`} data-testid={id('status')}>
                {'Excluded'}
              </Badge>
              {r.comment ? <span className="text-xs">{r.comment}</span> : null}
              {by ? <span className="text-[11px] text-muted-foreground">{by}</span> : null}
            </div>
          ) : (
            <Badge variant="success" className={`w-fit ${BADGE}`} data-testid={id('status')}>
              {'Gets emails'}
            </Badge>
          )}
        </td>
        {canEdit ? (
          <td className="py-1.5 text-right">
            <Button
              variant="outline"
              size="sm"
              className={SMALL_BTN}
              disabled={disabled}
              onClick={onAction}
              data-testid={id(r.excluded ? 'include-btn' : 'exclude-btn')}
            >
              {r.excluded ? 'Include again' : 'Exclude'}
            </Button>
          </td>
        ) : null}
      </tr>
      {editor ? (
        <tr>
          <td colSpan={canEdit ? 4 : 3}>{editor}</td>
        </tr>
      ) : null}
    </>
  );
}
